'use client';

import { FC, useCallback, useMemo } from 'react';

import { useAuthentication } from '@ravago/shared/authentication';
import { DynamicPageContentType } from '@ravago/shared/page-data/models/contentful';
import { NormalizedBusinessContext, NormalizedNavigationDropdown, NormalizedNavigationItem } from '@ravago/shared/page-data/models/contentful-mapped';
import { useConfigContext } from '@ravago/shared/page-elements-radiance/hooks/use-config-context';
import { DataTestSelector } from '@ravago/shared/page-elements-radiance/utils/format/format.utils';
import { NavigationUtils } from '@ravago/shared/page-elements-radiance/utils/navigation/navigation.utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { NavItem } from '@ravago/shared/radiance/components/navigation/components/NavItem/NavItem';
import { NavTab } from '@ravago/shared/radiance/components/navigation/components/NavTab/NavTab';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';

import { usePathname } from 'next/navigation';

interface Props {
  businessContext: NormalizedBusinessContext;
  locale: string;
  currentDropdown?: NormalizedNavigationDropdown;
  dynamicPageUrls?: { [key in DynamicPageContentType]?: string };
  onNavigate?: () => void;
  onDropdownClick?: (id: string) => void;
}

const Navigation: FC<Props> = ({ businessContext, locale, currentDropdown, dynamicPageUrls, onNavigate, onDropdownClick }) => {
  const pathname = usePathname();

  const { user } = useAuthentication();
  const { okta, linkLegacyDigitalPlatform, isPublic } = useConfigContext();

  const rlpHref = businessContext.registrationLandingPage?.page ? `/${locale}/${businessContext.registrationLandingPage.page.route ?? ''}` : businessContext.registrationLandingPage?.externalUrl ?? '';

  const colorIndex = businessContext.header?.navigationItems.findIndex((item) => 'id' in item && item.id === currentDropdown?.id) ?? 0;

  const getSubRoutes = (item: NormalizedNavigationDropdown) =>
    item.items.flatMap((subItem) => {
      if ('items' in subItem) {
        return subItem.items.map((subSubItem) => NavigationUtils.buildRoute(locale, subSubItem, dynamicPageUrls));
      }

      return [NavigationUtils.buildRoute(locale, subItem, dynamicPageUrls)];
    });

  const filteredNavItemsAndDropdowns = useMemo(() => NavigationUtils.filterItemsByAccessLevel(businessContext.header?.navigationItems || [], user), [businessContext, user]);

  const filteredNavItemsAndGroups = useMemo(() => (currentDropdown ? NavigationUtils.filterItemsByAccessLevel(currentDropdown.items, user) : []), [currentDropdown, businessContext, user]);

  const dropdownActive = useCallback((item: NormalizedNavigationDropdown) => item.id === currentDropdown?.id || getSubRoutes(item).includes(pathname), [currentDropdown, pathname]);

  return (
    <>
      <BreakpointSwitch includeViews={['desktop']}>
        <Box gap="none" height="100%">
          {filteredNavItemsAndDropdowns.map((item) =>
            'items' in item ? (
              <NavTab
                attributes={{ ...DataTestSelector('navigation-tab', item.label) }}
                key={(item as NormalizedNavigationDropdown).id}
                trailingIcon={(item as NormalizedNavigationDropdown).id === currentDropdown?.id ? 'chevron-up' : 'chevron-down'}
                active={dropdownActive(item as NormalizedNavigationDropdown)}
                onClick={() => onDropdownClick?.((item as NormalizedNavigationDropdown).id)}
              >
                {item.label}
              </NavTab>
            ) : (
              <NavTab
                attributes={{ ...DataTestSelector('navigation-tab', item.label) }}
                key={item.page?.route ?? item.externalUrl ?? 'current'}
                href={NavigationUtils.buildRoute(locale, item, dynamicPageUrls)}
                trailingIcon={item.openInNewTab ? 'external-link' : undefined}
                target={item.openInNewTab ? '_blank' : undefined}
                onClick={onNavigate}
                active={NavigationUtils.buildRoute(locale, item, dynamicPageUrls) === pathname}
              >
                {item.label}
              </NavTab>
            )
          )}
        </Box>
      </BreakpointSwitch>
      <BreakpointSwitch includeViews={['phone', 'tab-port', 'tab-land']}>
        {!currentDropdown && (
          <Box direction="column" height="100%" maxHeight="100%" justify="start" gap="sm" overflowY="auto">
            {filteredNavItemsAndDropdowns.map((item, index) =>
              'items' in item ? (
                <NavItem
                  attributes={{ ...DataTestSelector('navigation-item', item.label) }}
                  key={(item as NormalizedNavigationDropdown).id}
                  size="lg"
                  color={index}
                  onClick={() => onDropdownClick?.((item as NormalizedNavigationDropdown).id)}
                  icon={item.icon ?? undefined}
                  trailingIcon="chevron-right"
                  fullWidth
                >
                  {item.label}
                </NavItem>
              ) : (
                <NavItem
                  attributes={{ ...DataTestSelector('navigation-item', item.label) }}
                  href={NavigationUtils.buildRoute(locale, item, dynamicPageUrls)}
                  key={item.page?.route ?? item.externalUrl ?? ''}
                  size="lg"
                  color={index}
                  target={item.openInNewTab ? '_blank' : undefined}
                  onClick={onNavigate}
                  icon={item.icon ?? undefined}
                  trailingIcon={item.openInNewTab ? 'external-link' : undefined}
                >
                  {item.label}
                </NavItem>
              )
            )}

            {!user && linkLegacyDigitalPlatform && okta && !isPublic && businessContext.registrationLandingPage?.page && (
              <NavItem
                attributes={{ ...DataTestSelector('navigation-registration-link', businessContext.registrationLandingPage.label) }}
                href={rlpHref}
                size="lg"
                icon={businessContext.registrationLandingPage.icon ?? undefined}
                color={businessContext.header?.navigationItems.length || 0}
                target={businessContext.registrationLandingPage.openInNewTab ? '_blank' : undefined}
                onClick={onNavigate}
                trailingIcon={businessContext.registrationLandingPage.openInNewTab ? 'external-link' : undefined}
              >
                {businessContext.registrationLandingPage.label}
              </NavItem>
            )}
          </Box>
        )}

        {currentDropdown && (
          <Box direction="column" height="100%" width="100%" gap="md" overflowY="auto">
            <Box height="100%" width="100%" overflowY="auto" direction="column" gap="lg">
              {filteredNavItemsAndGroups.map((item) =>
                'items' in item ? (
                  <Box key={item.label} gap="sm" direction="column" width="100%">
                    <Typography variant="body-highlight">{item.label}</Typography>
                    <Box key={item.label} gap="xs" direction="column">
                      {NavigationUtils.filterItemsByAccessLevel(item.items, user).map((subItem) => (
                        <NavItem
                          attributes={{ ...DataTestSelector('navigation-item', (subItem as NormalizedNavigationItem).label) }}
                          href={NavigationUtils.buildRoute(locale, subItem, dynamicPageUrls)}
                          key={(subItem as NormalizedNavigationItem).label}
                          color={colorIndex}
                          target={(subItem as NormalizedNavigationItem).openInNewTab ? '_blank' : undefined}
                          onClick={onNavigate}
                          icon={(subItem as NormalizedNavigationItem).icon ?? undefined}
                          trailingIcon={(subItem as NormalizedNavigationItem).openInNewTab ? 'external-link' : undefined}
                        >
                          <Typography variant="body-highlight" color="strong">
                            {(subItem as NormalizedNavigationItem).label}
                          </Typography>
                          {(subItem as NormalizedNavigationItem).description && (
                            <Typography variant="small" color="subtle">
                              {(subItem as NormalizedNavigationItem).description}
                            </Typography>
                          )}
                        </NavItem>
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <NavItem
                    attributes={{ ...DataTestSelector('navigation-item', item.label) }}
                    href={item.page?.route ? `/${locale}/${item.page.route}` : item.externalUrl ?? ''}
                    key={item.label}
                    target={item.openInNewTab ? '_blank' : undefined}
                    color={colorIndex}
                    onClick={onNavigate}
                    icon={item.icon ?? undefined}
                    trailingIcon={item.openInNewTab ? 'external-link' : undefined}
                  >
                    <Typography variant="body-highlight" color="strong">
                      {item.label}
                    </Typography>
                    {item.description && (
                      <Typography variant="small" color="subtle">
                        {item.description}
                      </Typography>
                    )}
                  </NavItem>
                )
              )}
            </Box>
          </Box>
        )}
      </BreakpointSwitch>
    </>
  );
};

export default Navigation;
